const PURPOSE_KEYS = ['purposeOfRequest', 'purpose_of_request', 'purpose', 'reason'];

const NESTED_REQUEST_KEYS = ['accessRequest', 'access_request', 'request'];

/**
 * Purpose text entered with an access request.
 * Pending requests store it on `additionalInfo.purposeOfRequest`. Approved policies
 * keep that same object, sometimes nested under the original access request.
 */
export function readAccessRequestPurpose(
  item: Record<string, unknown> | null | undefined,
): string {
  if (!item) return '';
  for (const source of purposeSources(item)) {
    const purpose = readString(source, PURPOSE_KEYS);
    if (isUsablePurpose(purpose)) return purpose;
  }
  return '';
}

function purposeSources(item: Record<string, unknown>): Record<string, unknown>[] {
  const sources: Record<string, unknown>[] = [];
  const seen = new Set<Record<string, unknown>>();
  const add = (value: Record<string, unknown> | null): void => {
    if (!value || seen.has(value)) return;
    seen.add(value);
    sources.push(value);
  };

  add(readJsonObject(item, 'additionalInfo'));
  add(readJsonObject(item, 'additional_info'));
  for (const key of NESTED_REQUEST_KEYS) {
    const nested = readJsonObject(item, key);
    if (!nested) continue;
    add(readJsonObject(nested, 'additionalInfo'));
    add(readJsonObject(nested, 'additional_info'));
    add(nested);
  }
  add(item);
  return sources;
}

function readJsonObject(
  item: Record<string, unknown>,
  key: string,
): Record<string, unknown> | null {
  const value = item[key];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('{')) return null;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(item: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function isUsablePurpose(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return !!normalized && normalized !== 'n/a' && normalized !== 'na' && normalized !== '-';
}
